import { log, warn } from 'cc';
import Network, { NetworkEventType, RequestSetting } from './Network/Network';
import Delay from '../Toolkit/Utils/Delay';
import { String } from '../Toolkit/Utils/UtilityType';
import NetworkWebSocket from './Network/NetworkWebSocket';

export interface IBaseRequestResponseDataType {
    [PathName: string]: [any, any];
}

export interface IBasePublishDataType {
    [PathName: string]: any;
}

export type GetPublishDataType<T extends IBaseRequestResponseDataType> = { [TPathName in keyof T]: T[TPathName][1] };

export default class Connection<
    TRequestResponse extends IBaseRequestResponseDataType,
    TPublish extends IBasePublishDataType = GetPublishDataType<TRequestResponse>,
> {
    public static readonly EVENT_TYPE = Network.EVENT_TYPE;
    protected static readonly DEFAULT_TIMEOUT = 10000;
    private static readonly KEEP_ALIVE_INTERVAL = 1000;

    private _previousDelays: number[] = [];
    private _stopHeartBeatFunc: () => void = null;
    private _timeoutDuration: number = 0;
    private _isReconnecting: boolean = false;

    public constructor(
        private _network: Network,
        timeoutDuration: number = 0,
    ) {
        if (timeoutDuration > 0) {
            this._timeoutDuration = timeoutDuration;
        } else {
            this._timeoutDuration = Connection.DEFAULT_TIMEOUT;
        }
    }

    public get delay() {
        if (!this._previousDelays.length) {
            return 0;
        }
        return this._previousDelays.reduce((pre, cur) => pre + cur, 0) / this._previousDelays.length;
    }

    public get network() {
        return this._network;
    }

    public get isReconnecting() {
        return this._isReconnecting;
    }

    public on(type: NetworkEventType.DISCONNECTED, callback: () => void, thisArg?: any, once?: boolean): () => void;
    public on(
        type: NetworkEventType.ERROR,
        callback: (error: string, subject: string, data: any, path: string) => void,
        thisArg?: any,
        once?: boolean,
    ): () => void;
    public on<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey], subject: string) => void>(
        type: String<TKey>,
        callback: TFunction,
        thisArg?: any,
        once?: boolean,
    ): TFunction;
    public on<TKey extends keyof TPublish, TFunction extends (error: string, subject: string, data: any, path: string) => void>(
        type: `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
        once?: boolean,
    ): TFunction;
    public on<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey], subject: string) => void>(
        type: NetworkEventType.DISCONNECTED | NetworkEventType.ERROR | String<TKey> | `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
        once?: boolean,
    ): TFunction {
        return this._network.on(type, callback, thisArg, once);
    }

    public once(type: 'disconnect', callback: () => void, thisArg?: any): () => void;
    public once(type: NetworkEventType.ERROR, callback: (error: string, subject: string, data: any) => void, thisArg?: any): () => void;
    public once<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey], subject: string) => void>(
        type: String<TKey>,
        callback: TFunction,
        thisArg?: any,
    ): TFunction;
    public once<TKey extends keyof TPublish, TFunction extends (error: string, subject: string, data: any) => void>(
        type: `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
    ): TFunction;
    public once<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey] | string, subject: string) => void>(
        type: NetworkEventType.DISCONNECTED | NetworkEventType.ERROR | String<TKey> | `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
    ): TFunction {
        return this._network.once(type, callback, thisArg);
    }

    public off(type: 'disconnect', callback: () => void, thisArg?: any): void;
    public off(type: NetworkEventType.ERROR, callback: (error: string, subject: string, data: any) => void, thisArg?: any): void;
    public off<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey], subject: string) => void>(
        type: String<TKey>,
        callback: TFunction,
        thisArg?: any,
    ): void;
    public off<TKey extends keyof TPublish, TFunction extends (error: string, subject: string, data: any) => void>(
        type: `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
    ): void;
    public off<TKey extends keyof TPublish, TFunction extends (data: TPublish[TKey] | string, subject: string) => void>(
        type: NetworkEventType.DISCONNECTED | NetworkEventType.ERROR | String<TKey> | `${String<TKey>}_error`,
        callback: TFunction,
        thisArg?: any,
    ): void {
        return this._network.off(type, callback, thisArg);
    }

    public targetOff(typeOrTarget: any) {
        return this._network.targetOff(typeOrTarget);
    }

    public removeAll(typeOrTarget: any) {
        return this._network.removeAll(typeOrTarget);
    }

    public async connect() {
        await this._network.open();
    }

    public async reconnect(url?: string) {
        this._isReconnecting = true;
        this.stopHeartBeating();
        await this._network.reconnect(url);
        this._isReconnecting = false;
    }

    public send<TKey extends keyof TRequestResponse>(pathName: String<TKey>): Promise<TRequestResponse[TKey][1]>;
    public send<TKey extends keyof TRequestResponse>(pathName: String<TKey>, postData: TRequestResponse[TKey][0]): Promise<TRequestResponse[TKey][1]>;
    public send<TKey extends keyof TRequestResponse>(
        pathName: String<TKey>,
        postData: TRequestResponse[TKey][0],
        setting: RequestSetting,
    ): Promise<TRequestResponse[TKey][1]>;
    public send<TKey extends keyof TRequestResponse>(
        pathName: String<TKey>,
        postData: TRequestResponse[TKey][0],
        setting: RequestSetting,
        subject: string,
    ): Promise<TRequestResponse[TKey][1]>;
    public send<TKey extends keyof TRequestResponse>(
        pathName: String<TKey>,
        postData?: TRequestResponse[TKey][0],
        setting?: RequestSetting,
        subject?: string,
    ): Promise<TRequestResponse[TKey][1]> {
        return this._network.send(pathName, postData, { ...{ expectedErrors: [], timeout: this._timeoutDuration, noReply: false }, ...setting }, subject);
    }

    public disconnect() {
        this._network.close();
    }

    public startHeartBeating(ackPath: string) {
        if (this._stopHeartBeatFunc) {
            warn('Heart beating already started');
            return;
        }
        this._heartBeat(ackPath);
    }

    public stopHeartBeating() {
        if (this._stopHeartBeatFunc) {
            this._stopHeartBeatFunc();
            this._stopHeartBeatFunc = null;
        }
    }

    private async _heartBeat(ackPath: string) {
        try {
            const requestTime = Date.now();
            if (this._network instanceof NetworkWebSocket && this._network.isClosed) {
                return;
            }
            await Promise.race([
                this.send(ackPath, undefined, { timeout: this._timeoutDuration + this.delay }),
                new Promise((_, reject) => {
                    this._stopHeartBeatFunc = reject;
                }),
            ]);
            this._previousDelays.push(Date.now() - requestTime);
            if (this._previousDelays.length > 20) {
                this._previousDelays.shift();
            }
            await Promise.race([
                Delay.resolve(Connection.KEEP_ALIVE_INTERVAL),
                new Promise((_, reject) => {
                    this._stopHeartBeatFunc = reject;
                }),
            ]);
            this._heartBeat(ackPath);
        } catch {
            log('stop heart beating');
        }
    }
}
